/** Widget booking API payloads (section 11.1), served next to the other widget endpoints. */
import { z } from "zod";
import { consultationModeSchema } from "../firestore/booking.js";
import { e164Schema, emailSchema, localeSchema } from "../common.js";
// ---- GET /v1/widget/booking/options ----
export const bookingOptionsResponseSchema = z.object({
    timezone: z.string(),
    types: z.array(z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().optional(),
        mode: consultationModeSchema,
        durationMinutes: z.number().int(),
        price: z.number().int(),
        currency: z.string(),
        doctorIds: z.array(z.string()),
        /** In-person types: locations where it is offered (ids from `locations`); empty = all. */
        locationIds: z.array(z.string()).default([]),
        cancellationPolicyText: z.string().optional(),
        /** False when a paid type cannot be taken (the business has not connected Stripe yet). */
        bookable: z.boolean(),
    })),
    doctors: z.array(z.object({
        id: z.string(),
        name: z.string(),
        title: z.string().optional(),
        photoUrl: z.string().optional(),
        /** Locations this specialist works at; empty = every location. */
        locationIds: z.array(z.string()).default([]),
    })),
    /** Active locations for in-person appointments; the chat asks which one when there are several. */
    locations: z
        .array(z.object({ id: z.string(), name: z.string(), address: z.string() }))
        .default([]),
    /** Card payment inside the chat is available (Stripe connected and charges enabled). */
    payments: z.boolean().default(false),
    /** Active services from the catalogue, grouped by category in the chat (owner request, 14 October 2026). */
    procedures: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        category: z.string(),
        /** Locations that offer it; empty = every location (15 October 2026). */
        locationIds: z.array(z.string()).default([]),
    }))
        .default([]),
});
// ---- GET /v1/widget/booking/slots?type=&doctor=&from=&days= ----
export const slotsQuerySchema = z.object({
    type: z.string().min(1).max(64),
    doctor: z.string().max(64).optional(),
    location: z.string().max(64).optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    days: z.coerce.number().int().min(1).max(14).default(7),
});
export const slotsResponseSchema = z.object({
    timezone: z.string(),
    days: z.array(z.object({
        date: z.string(),
        slots: z.array(z.object({ startAt: z.string(), doctorId: z.string() })),
    })),
});
// ---- POST /v1/widget/leads/current/booking ----
export const createBookingRequestSchema = z.object({
    consultationTypeId: z.string().min(1).max(64),
    doctorId: z.string().max(64).optional(),
    locationId: z.string().max(64).optional(),
    /** Procedure names the patient picked (catalogue names; unknown ones are dropped). */
    procedures: z.array(z.string().max(120)).max(20).optional(),
    startAt: z.string().datetime({ offset: true }),
    patientTimezone: z.string().max(64).optional(),
    atNodeId: z.string().max(64).optional(),
});
export const bookingSummarySchema = z.object({
    appointmentId: z.string(),
    procedures: z.array(z.string()).optional(),
    status: z.enum(["held", "confirmed", "cancelled", "completed", "no_show"]),
    startAt: z.string(),
    endAt: z.string(),
    timezone: z.string(),
    type: z.object({
        name: z.string(),
        mode: consultationModeSchema,
        durationMinutes: z.number().int(),
        price: z.number().int(),
        currency: z.string(),
        cancellationPolicyText: z.string().optional(),
    }),
    doctor: z.object({ name: z.string(), title: z.string().optional() }),
    address: z.string().optional(),
    /** In-person appointments at a clinic with several locations. */
    location: z.object({ name: z.string(), address: z.string() }).optional(),
    meetingUrl: z.string().optional(),
    calendar: z.object({ google: z.string(), ics: z.string() }),
});
// ---- staff callables (dashboard) ----
/** `createAppointment`: a staff member books for an existing lead (section 11, manual appointments). */
export const createAppointmentRequestSchema = z
    .object({
    /** Existing lead, or a new person entered by staff (phone or walk-in; owner request, 14 October 2026). */
    leadId: z.string().min(1).max(64).optional(),
    newLead: z
        .object({
        fullName: z.string().trim().min(1).max(200),
        phone: z.string().max(40).optional(),
        email: z.string().max(254).optional(),
    })
        .optional(),
    consultationTypeId: z.string().min(1).max(64),
    doctorId: z.string().min(1).max(64),
    locationId: z.string().max(64).optional(),
    /** Procedure names (from the catalogue) noted on the appointment. */
    procedures: z.array(z.string().max(120)).max(20).optional(),
    startAt: z.string().datetime({ offset: true }),
    /** Book outside working hours (staff only); clashes with other appointments are still refused. */
    force: z.boolean().default(false),
})
    .refine((v) => Boolean(v.leadId) !== Boolean(v.newLead), {
    message: "leadId or newLead, not both",
});
/** `rescheduleAppointment`: moves a live appointment to a new start time (and, since 14 October 2026, optionally to another specialist or location). */
export const rescheduleAppointmentRequestSchema = z.object({
    appointmentId: z.string().min(1).max(64),
    startAt: z.string().datetime({ offset: true }),
    doctorId: z.string().max(64).optional(),
    locationId: z.string().max(64).optional(),
    procedures: z.array(z.string().max(120)).max(20).optional(),
    force: z.boolean().default(false),
});
/** `staffSlots`: free slots for the picker in the dashboard. */
export const staffSlotsRequestSchema = z.object({
    consultationTypeId: z.string().min(1).max(64),
    doctorId: z.string().max(64).optional(),
    locationId: z.string().max(64).optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    days: z.number().int().min(1).max(14).default(7),
});
// ---- POST /v1/widget/leads/current/booking/hold (paid types) ----
/** Holds the slot and returns what Stripe.js needs to take the payment inside the chat (11.3). */
export const holdBookingResponseSchema = z.object({
    appointmentId: z.string(),
    clientSecret: z.string(),
    stripeAccountId: z.string(),
    publishableKey: z.string(),
    amount: z.number().int(),
    currency: z.string(),
    holdExpiresAt: z.string(),
});
// ---- GET /v1/widget/booking/status?appointment= ----
export const bookingStatusResponseSchema = z.object({
    status: z.enum(["held", "confirmed", "cancelled", "completed", "no_show"]),
    paymentStatus: z.enum(["none", "pending", "paid", "refunded"]),
    summary: bookingSummarySchema.optional(),
});
// ---- Booking page (owner request, 15 October 2026) ----
// A classic booking form at widget/book/<slug>: location → treatment → date and time → details →
// payment. Options and free times carry no personal data and need no session; the booking
// itself creates the lead (with consent) or uses the chat's session when opened from the chat.
/** GET /v1/widget/book/options?clinic=ID | ?slug=SLUG */
export const bookingPageOptionsResponseSchema = bookingOptionsResponseSchema.extend({
    clinic: z.object({
        id: z.string(),
        name: z.string(),
        slug: z.string(),
        color: z.string(),
        defaultLocale: localeSchema,
        /** The clinic's privacy notice, linked from the consent line when the chat funnel has one. */
        privacyUrl: z.string().optional(),
        /** The clinic takes enquiries by email: the confirmation offers "Send by email" too. */
        emailHandoff: z.boolean().optional(),
    }),
    /** False when the clinic has not switched internal booking on (the page says so). */
    open: z.boolean(),
});
/** GET /v1/widget/book/slots?clinic=&type=&doctor=&location=&from=&days= */
export const bookingPageSlotsQuerySchema = slotsQuerySchema.extend({
    clinic: z.string().min(1).max(64),
});
/** POST /v1/widget/book */
export const bookingPageRequestSchema = createBookingRequestSchema.extend({
    clinicId: z.string().min(1).max(64),
    contact: z.object({
        firstName: z.string().trim().min(1).max(60),
        lastName: z.string().trim().min(1).max(60),
        email: emailSchema,
        phone: e164Schema,
    }),
    note: z.string().trim().max(800).optional(),
    /** D4: given on the details step, before anything personal is stored. */
    consent: z.object({ data_processing: z.literal(true), marketing: z.boolean().optional() }),
    locale: localeSchema.optional(),
    pageUrl: z.string().max(2000).optional(),
});
//# sourceMappingURL=booking.js.map